/**
 * Auction phase classification — Phase 07b.
 *
 * The old `AuctionShowcaseClient` computed four phases: `upcoming`, `live`,
 * `ended`, `canceled`. `pages/AUCTIONS.md` §11/§22 requires six, distinct:
 * upcoming, live, ENDING SOON, ended, canceled, and FINALISING — a real
 * interval between "the clock hit zero" and "the outcome is settled" that the
 * old logic collapsed straight into "ended".
 *
 * ENDING SOON is time-based and safe to invent a threshold for — it is a
 * presentation choice about an already-real countdown, not a fabricated
 * business fact. 15 minutes is chosen the same way `pages/DROPS.md`'s "< 10
 * days → live countdown" threshold was: a round, documented, easily-changed
 * constant, not a value read from any API.
 *
 * FINALISING is NOT invented the same way. Whether the backend exposes a
 * settlement/finalisation state was Not verified in `pages/AUCTIONS.md` §28,
 * and a repository search here confirms it: `AuctionItem`/`BidItem` carry no
 * settlement field, and `features/auctions/api.ts` exposes no endpoint for
 * one. So `finalising` here means exactly one thing — the clock has reached
 * zero on an auction that is still `status: "active"` and has not yet been
 * flipped to `finished` by the backend — and nothing about WHAT settlement
 * does or how long it takes is asserted. The caller must render this as an
 * explicit unavailable/pending state, never as a fabricated progress bar.
 */

export type AuctionRawStatus = "draft" | "active" | "settling" | "finished" | "canceled";

/** `settling` is the backend's own: a winner is chosen and the transfer and payment run on chain (./settlement). */
export type AuctionPhase = "upcoming" | "live" | "ending-soon" | "finalising" | "settling" | "ended" | "canceled";

/** Presentation-only threshold — not a business rule, not read from any API. */
export const ENDING_SOON_MS = 15 * 60_000;

export type PhaseInput = {
  status: AuctionRawStatus;
  startTimeMs: number | null;
  expireTimeMs: number | null;
  nowMs: number;
};

export function getAuctionPhase({ status, startTimeMs, expireTimeMs, nowMs }: PhaseInput): AuctionPhase {
  // Explicit backend status wins over anything time-derived: a "finished" or
  // "canceled" auction cannot be un-classified by clock skew or a stale
  // start/expire time.
  if (status === "canceled") return "canceled";
  if (status === "finished") return "ended";
  if (status === "settling") return "settling";

  const started = startTimeMs !== null && nowMs >= startTimeMs;
  const expired = expireTimeMs !== null && nowMs >= expireTimeMs;

  if (!started) return "upcoming";
  // The clock ended but the backend has not (yet) reported "finished" —
  // exactly the real, unresolved interval AUCTIONS.md §11 names, not a
  // fourth "ended" bucket that pretends the outcome is already known.
  if (expired) return "finalising";
  if (expireTimeMs !== null && expireTimeMs - nowMs <= ENDING_SOON_MS) return "ending-soon";
  return "live";
}

/** A countdown target only exists for the two phases with a clock still running. */
export function countdownTargetFor(phase: AuctionPhase, startTimeMs: number | null, expireTimeMs: number | null) {
  if (phase === "upcoming") return startTimeMs;
  if (phase === "live" || phase === "ending-soon") return expireTimeMs;
  return null;
}
